import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Injector, Input, NgZone, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import type { UploadTask } from 'firebase/storage';
import { AbstractComponent, ChatContextRef, FirebaseService, SocketService, UploadTypeEnum } from 'lib-trend-core';
import { distinctUntilChanged, take, takeUntil } from 'rxjs/operators';
import { ChatContextRefService } from '../chat-context-ref/chat-context-ref.service';
import { ChatComposerDraft, ChatComposerDraftMention } from '../state/chat.state';
import {
  CHAT_MAX_ATTACHMENTS_PER_MESSAGE,
  CHAT_MAX_FILE_SIZE_BYTES,
  chatFileIcon,
  ChatSendAttachment,
  formatChatFileSize,
  isBlockedChatFileExtension,
} from './chat-attachment.util';

/** Backend hard limit mirrored client-side (RNF-06). */
export const CHAT_MESSAGE_MAX_LENGTH = 12_000;

/** The counter appears once the draft crosses this length. */
export const CHAT_COUNTER_VISIBLE_FROM = 11_000;

/** localStorage key persisting the formatting-toolbar visibility (Aa toggle). */
const TOOLBAR_STORAGE_KEY = 'trendmulti.chat.composerToolbar';

const MENTION_MAX_QUERY_LENGTH = 30;
const MENTION_MAX_OPTIONS = 8;

/** Hard cap for one voice recording — auto-stops at 5 minutes (T-34). */
export const CHAT_RECORDING_MAX_SECONDS = 300;

/**
 * Preferred MediaRecorder containers, best first. Chromium/Firefox support
 * WebM/Opus; Safari records audio/mp4 (AAC) — the first supported entry wins.
 */
const CHAT_AUDIO_MIME_CANDIDATES: string[] = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

/** File extension per recorded container (base MIME, parameters stripped). */
const CHAT_AUDIO_EXTENSION_BY_MIME: Record<string, string> = {
  'audio/webm': '.webm',
  'audio/mp4': '.m4a',
  'audio/ogg': '.ogg',
};

/** Member offered by the mention autocomplete (provided by the host pane). */
export interface ChatMentionMember {
  id: string;
  name: string;
  image?: string;
}

/** `(send)` payload — content + the user ids actually mentioned in it. */
export interface ChatComposerSend {
  content: string;
  /** User ids whose `@Display Name` token is still present in the content */
  mentions: string[];
  /** Uploaded attachments riding the MESSAGE_SEND payload (T-33, plan §8). */
  attachments: ChatSendAttachment[];
  /**
   * Work reference attached to this message (T-22/T-24, RF-35). The backend
   * re-hydrates the snapshot from `appdb` at send time — the client snapshot is
   * only a fallback when the record cannot be resolved.
   */
  contextRef?: ChatContextRef | null;
}

/** Pending-tray entry — one per file, uploading in parallel (T-33). */
export interface ChatPendingAttachment {
  /** Local tray id (not the message id). */
  id: string;
  name: string;
  size: number;
  sizeLabel: string;
  contentType: string;
  icon: string;
  /** Object URL thumbnail — images only (revoked on remove/submit/destroy). */
  previewUrl?: string;
  progress: number;
  status: 'uploading' | 'done' | 'error';
  /** Storage download URL — set when the upload settles. */
  fileUrl?: string;
  task?: UploadTask;
  /** Cancel/remove marker — silences the settle callbacks of a dead upload. */
  removed?: boolean;
}

/**
 * Internal Chat rich composer (SPEC-2026-07-internal-chat, T-19).
 *
 * Textarea (Enter sends, Shift+Enter breaks the line, autogrow) plus:
 * - formatting toolbar producing the markdown subset rendered by
 *   ChatMarkdownPipe (wrap-selection behavior, Aa toggle persisted);
 * - mention autocomplete over the active conversation's members
 *   (`@` + query → Slack-style dropdown → `@Display Name` + collected id);
 * - curated emoji popover inserting at the caret;
 * - 12k char counter (visible after 11k, blocks at the limit);
 * - offline chip while the socket is disconnected (commands queue FIFO in
 *   ChatSocketService and flush on reconnect).
 *
 * T-33 additions — attachments:
 * - paperclip file picker (multiple) + paste-to-attach on the textarea; the
 *   host pane also feeds drag-and-drop files through addFiles();
 * - client-side limits BEFORE upload (100 MB/file, 10 files/message, blocked
 *   executable extensions) mirroring the backend chat-content.util constants;
 * - pending tray with per-file thumbnail/icon, parallel Firebase uploads
 *   (Firebase Storage upload task — progress events + cancel), path
 *   `chat/{companyId}/{channelId}/…` via UploadTypeEnum.CHAT;
 * - submit emits the uploaded attachments[] alongside content — the backend
 *   persists ONE message per attachment (+ one TEXT message when content
 *   exists); attachments are transient (never part of the persisted draft).
 *
 * T-34 additions — voice recording:
 * - mic button flips the composer into recording mode (red pulse dot, mm:ss
 *   timer, live level hint via AnalyserNode driven OUTSIDE Angular — the rAF
 *   loop mutates the level bar transform directly, zero change detection);
 * - MediaRecorder with container fallback (WebM/Opus → mp4/AAC on Safari);
 * - cancel discards silently; send hands the blob to the T-33 upload pipeline
 *   as a regular AUDIO attachment and auto-submits once THAT upload settles
 *   (any other pending upload keeps it in the tray for a manual send);
 * - permission denied / no device → localized guidance (never a raw error);
 * - hard 5-minute cap auto-stops into the tray WITHOUT auto-send.
 *
 * T-22 addition (wired by T-24) — work references (RF-35):
 * - the link button opens the Attendance/Card/Contact picker; the chosen
 *   reference sits in a removable card above the input and rides the next
 *   MESSAGE_SEND / THREAD_REPLY payload as `contextRef` (transient, like the
 *   attachments — never persisted into the draft).
 *
 * Reused by the full-view conversation pane AND the bubble popup quick reply
 * (one component, no duplication). Draft persistence is host-driven: the pane
 * snapshots via getDraftSnapshot()/setDraftSnapshot() into the store (T-19).
 */
@Component({
  selector: 'chat-composer',
  templateUrl: './chat-composer.component.html',
  styleUrls: ['./chat-composer.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatComposerComponent extends AbstractComponent implements OnInit, AfterViewInit {

  @ViewChild('composerInput') composerInput: ElementRef<HTMLTextAreaElement>;
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;
  @ViewChild('recorderLevel') recorderLevel: ElementRef<HTMLSpanElement>;
  private widthObserver?: ResizeObserver;
  private lastWidth = 0;

  @Input() disabled = false;

  /** Mentionable members of the active conversation (host-provided). */
  @Input() members: ChatMentionMember[] = [];

  /** Active channel id — segments the upload path (`direct` while a DM draft). */
  @Input() channelId: string | null = null;

  @Output() send = new EventEmitter<ChatComposerSend>();
  @Output() typingChange = new EventEmitter<boolean>();
  /**
   * ArrowUp on an EMPTY field — "edit my last message" (the Slack reflex).
   * Only emitted when someone listens (`observed`): the thread composer leaves
   * it unbound, so there ArrowUp keeps its native caret behavior and the hint
   * below stays hidden.
   */
  @Output() editLast = new EventEmitter<void>();
  /** Fires on every content mutation — host debounces the draft persistence. */
  @Output() draftChange = new EventEmitter<void>();

  readonly maxLength = CHAT_MESSAGE_MAX_LENGTH;
  readonly counterVisibleFrom = CHAT_COUNTER_VISIBLE_FROM;

  /** Curated grid — no external emoji dependency (T-19 point 4). */
  readonly emojis: string[] = [
    '😀', '😄', '😁', '😆', '😅', '😂',
    '🙂', '😉', '😊', '😍', '🤩', '😘',
    '🤔', '🤨', '😐', '😏', '😴', '😎',
    '😢', '😭', '😤', '😡', '🤯', '🥳',
    '👍', '👎', '👏', '🙏', '🤝', '💪',
    '👀', '🎉', '🔥', '❤️', '✅', '🚀',
  ];

  draft = '';
  toolbarVisible = false;
  offline = false;

  /** Pending-attachments tray (T-33) — parallel uploads, transient (not drafted). */
  attachments: ChatPendingAttachment[] = [];

  /**
   * Work reference picked for the NEXT message (T-22/T-24 · RF-35). Transient
   * like the attachments — never part of the persisted draft, cleared on send.
   */
  pendingContextRef: ChatContextRef | null = null;

  // Voice recording state (T-34) --------------------------------------------
  recording = false;
  recordingSeconds = 0;
  private mediaRecorder: MediaRecorder | null = null;
  private mediaStream: MediaStream | null = null;
  private recordedChunks: Blob[] = [];
  /** Cancel marker — a discarded recording produces no attachment. */
  private recordingDiscarded = false;
  /** Explicit send (mic ✓) auto-submits when the upload settles; cap does not. */
  private recordingAutoSend = false;
  private recordingTimer: ReturnType<typeof setInterval> | null = null;
  /** Level-hint plumbing — closed/cancelled on every stop path. */
  private audioContext: AudioContext | null = null;
  private levelRafId: number | null = null;
  /** Tray id of the recording whose settle should auto-submit the message. */
  private autoSendAttachmentId: string | null = null;

  // Mention autocomplete state -------------------------------------------------
  mentionOpen = false;
  mentionOptions: ChatMentionMember[] = [];
  mentionActiveIndex = 0;
  /** index of the `@` that opened the dropdown (into `draft`). */
  private mentionStartIndex = -1;

  /** Mentions inserted while composing — resolved to ids on submit. */
  private pendingMentions: ChatComposerDraftMention[] = [];

  constructor(
    injector: Injector,
    private readonly socketService: SocketService,
    private readonly firebaseService: FirebaseService,
    private readonly zone: NgZone,
    private readonly chatContextRefService: ChatContextRefService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.toolbarVisible = this.readToolbarPreference();

    this.socketService.getConnectedStatus()
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((connected) => this.offline = !connected);
  }

  ngAfterViewInit(): void {
    this.autoGrow();
    this.observeWidth();
  }

  override ngOnDestroy(): void {
    this.widthObserver?.disconnect();
    // A recording in flight dies silently with the component (T-34).
    this.stopRecorder(true, false);
    // Cancel in-flight uploads and release thumbnail object URLs.
    for (const attachment of this.attachments) {
      this.disposeAttachment(attachment);
    }
    this.attachments = [];
    super.ngOnDestroy();
  }

  // ---------------------------------------------------------------------------
  // View-model
  // ---------------------------------------------------------------------------

  get canSend(): boolean {
    if (this.draft.length > this.maxLength) return false;
    if (this.attachments.length > 0) {
      // Attachments allow an empty text body, but every upload must be settled
      // successfully first (failed chips must be removed or nothing sends).
      return this.attachments.every((attachment) => attachment.status === 'done');
    }
    return this.draft.trim().length > 0;
  }

  get uploading(): boolean {
    return this.attachments.some((attachment) => attachment.status === 'uploading');
  }

  get counterVisible(): boolean {
    return this.draft.length >= this.counterVisibleFrom;
  }

  get atLimit(): boolean {
    return this.draft.length >= this.maxLength;
  }

  /** mm:ss elapsed recording time (T-34). */
  get recordingLabel(): string {
    const minutes = Math.floor(this.recordingSeconds / 60).toString().padStart(2, '0');
    const seconds = (this.recordingSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  }

  // ---------------------------------------------------------------------------
  // Draft snapshot API (host-driven persistence — T-19 point 7)
  // ---------------------------------------------------------------------------

  getDraftSnapshot(): ChatComposerDraft {
    return {
      content: this.draft,
      mentions: this.pendingMentions.filter((mention) => this.draft.includes(`@${mention.name}`)),
    };
  }

  setDraftSnapshot(draft: ChatComposerDraft | null | undefined): void {
    this.draft = draft?.content ?? '';
    this.pendingMentions = [...(draft?.mentions ?? [])];
    // The host only restores a snapshot when the hosted conversation CHANGES
    // (ngOnChanges) or on first mount. Transient state is per-conversation, so
    // it must not survive the switch: a work reference picked for conversation
    // A — or a file uploaded under `chat/{company}/A/…` — must never ride a
    // message sent to conversation B (T-24).
    this.pendingContextRef = null;
    for (const attachment of this.attachments) {
      this.disposeAttachment(attachment);
    }
    this.attachments = [];
    this.autoSendAttachmentId = null;
    this.closeMention();
    setTimeout(() => this.autoGrow());
  }

  // ---------------------------------------------------------------------------
  // Textarea events
  // ---------------------------------------------------------------------------

  onKeydown(event: KeyboardEvent): void {
    if (this.mentionOpen) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.mentionActiveIndex = (this.mentionActiveIndex + 1) % this.mentionOptions.length;
          return;
        case 'ArrowUp':
          event.preventDefault();
          this.mentionActiveIndex =
            (this.mentionActiveIndex - 1 + this.mentionOptions.length) % this.mentionOptions.length;
          return;
        case 'Enter':
        case 'Tab':
          event.preventDefault();
          this.selectMention(this.mentionOptions[this.mentionActiveIndex]);
          return;
        case 'Escape':
          // Swallowed so the popup shell never treats it as "close chat".
          event.preventDefault();
          event.stopPropagation();
          this.closeMention();
          return;
        default:
          break;
      }
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.submit();
      return;
    }

    // Empty field only — with text present ArrowUp is caret movement, and the
    // mention navigator above already claimed its case.
    if (
      event.key === 'ArrowUp' &&
      !this.draft.trim() &&
      this.editLast.observed
    ) {
      event.preventDefault();
      this.editLast.emit();
    }
  }

  onInput(): void {
    this.typingChange.emit(this.draft.trim().length > 0);
    this.autoGrow();
    this.updateMentionState();
    this.draftChange.emit();
  }

  /** Caret moved without input (click / arrow keys) — refresh the `@` context. */
  onCaretMove(): void {
    this.updateMentionState();
  }

  onBlur(): void {
    this.typingChange.emit(false);
  }

  /** Paste-to-attach (T-33) — clipboard files (screenshots) become attachments. */
  onPaste(event: ClipboardEvent): void {
    const files = event.clipboardData?.files;
    if (!files || files.length === 0) return;
    event.preventDefault();
    event.stopPropagation();
    this.addFiles(files);
  }

  submit(): void {
    if (this.disabled || !this.canSend) return;

    const content = this.draft.trim();
    const attachments: ChatSendAttachment[] = this.attachments
      .filter((attachment) => attachment.status === 'done' && !!attachment.fileUrl)
      .map((attachment) => ({
        fileUrl: attachment.fileUrl,
        contentType: attachment.contentType,
        fileName: attachment.name,
        fileSize: attachment.size,
      }));

    const mentions = Array.from(new Set(
      this.pendingMentions
        .filter((mention) => content.includes(`@${mention.name}`))
        .map((mention) => mention.id),
    ));

    this.send.emit({ content, mentions, attachments, contextRef: this.pendingContextRef });
    this.draft = '';
    this.pendingMentions = [];
    this.pendingContextRef = null;
    // Files are already in storage — only the thumbnails need releasing.
    for (const attachment of this.attachments) {
      this.revokePreview(attachment);
    }
    this.attachments = [];
    this.closeMention();
    this.typingChange.emit(false);
    this.autoGrow();
    this.composerInput?.nativeElement?.focus();
  }

  // ---------------------------------------------------------------------------
  // Work reference (T-22 picker, wired here by T-24 · RF-35 / AC-31)
  // ---------------------------------------------------------------------------

  /**
   * Opens the Attendance/Card/Contact picker and parks the chosen reference on
   * the next outgoing message. `openPicker()` is used instead of the service's
   * hot `selection$` on purpose: several composers can be mounted at once (the
   * main pane plus the thread panel), and a broadcast selection would attach
   * the reference to every one of them.
   */
  attachContextRef(): void {
    if (this.disabled) return;
    this.chatContextRefService.openPicker(this.channelId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((reference) => {
        if (reference) this.pendingContextRef = reference;
      });
  }

  /** Tray × on the pending reference card. */
  clearContextRef(): void {
    this.pendingContextRef = null;
  }

  // ---------------------------------------------------------------------------
  // Attachments (T-33) — validate BEFORE upload, parallel tasks with progress
  // ---------------------------------------------------------------------------

  openFilePicker(): void {
    if (this.disabled) return;
    this.fileInput?.nativeElement?.click();
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input?.files?.length) {
      this.addFiles(input.files);
    }
    // Reset so picking the same file again re-triggers `change`.
    if (input) input.value = '';
  }

  /**
   * Shared entry point for the picker, drag-and-drop (host-driven) and paste.
   * Client-side limits run BEFORE any byte is uploaded (RNF-06): blocked
   * executable extensions, 100 MB per file, 10 files per message.
   */
  addFiles(files: FileList | File[]): void {
    if (this.disabled) return;

    const incoming = Array.from(files ?? []);
    if (incoming.length === 0) return;

    const rejectionKeys = new Set<string>();
    const accepted: File[] = [];

    for (const file of incoming) {
      if (isBlockedChatFileExtension(file.name)) {
        rejectionKeys.add('chat.errors.blockedFileExtension');
        continue;
      }
      if (file.size > CHAT_MAX_FILE_SIZE_BYTES) {
        rejectionKeys.add('chat.errors.fileTooLarge');
        continue;
      }
      if (this.attachments.length + accepted.length >= CHAT_MAX_ATTACHMENTS_PER_MESSAGE) {
        rejectionKeys.add('chat.errors.tooManyAttachments');
        continue;
      }
      accepted.push(file);
    }

    for (const key of rejectionKeys) {
      this.alertService.error(this.t(key));
    }

    for (const file of accepted) {
      this.startUpload(file);
    }
  }

  /** Tray chip × — cancels an in-flight upload or discards a settled file. */
  removeAttachment(attachment: ChatPendingAttachment): void {
    if (this.autoSendAttachmentId === attachment.id) {
      // The recording chip was removed — its settle must not auto-submit.
      this.autoSendAttachmentId = null;
    }
    this.disposeAttachment(attachment);
    this.attachments = this.attachments.filter((item) => item.id !== attachment.id);
  }

  private startUpload(file: File): ChatPendingAttachment {
    const contentType = file.type || 'application/octet-stream';
    const attachment: ChatPendingAttachment = {
      id: this.generateAttachmentId(),
      name: file.name,
      size: file.size,
      sizeLabel: formatChatFileSize(file.size),
      contentType,
      icon: chatFileIcon(contentType, file.name),
      previewUrl: contentType.startsWith('image/') ? URL.createObjectURL(file) : undefined,
      progress: 0,
      status: 'uploading',
    };
    this.attachments = [...this.attachments, attachment];

    // Upload path per plan §8: chat/{companyId}/{channelId}/{unique}-{name}
    // (`direct` segment while a DM is still a draft — no channel id yet).
    const safeName = file.name.replace(/[^\w.\-]+/g, '_');
    const segment = this.channelId ?? 'direct';
    const filePath = `${UploadTypeEnum.CHAT}${this.getIDCurrentCompany()}/${segment}/${attachment.id}-${safeName}`;

    const task = this.firebaseService.startUpload(filePath, file);
    attachment.task = task;

    const failed = () => this.zone.run(() => {
      if (attachment.removed) return;
      attachment.status = 'error';
      this.clearAutoSend(attachment);
    });

    // The task is kept on the attachment for cancel(); the download URL is
    // read once the upload completes.
    const stopListening = task.on(
      'state_changed',
      (snapshot) => this.zone.run(() => {
        if (attachment.removed) return;
        attachment.progress = snapshot.totalBytes
          ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
          : 0;
      }),
      failed,
      () => {
        this.firebaseService.getDownloadUrl(task).then(
          (url) => this.zone.run(() => {
            if (attachment.removed) return;
            attachment.fileUrl = url;
            attachment.progress = 100;
            attachment.status = 'done';
            this.maybeAutoSend(attachment);
          }),
          failed,
        );
      },
    );
    this.destroy$.pipe(take(1)).subscribe(() => stopListening());

    return attachment;
  }

  /**
   * Voice-note settle hook (T-34): the recording sent via the mic ✓ button
   * auto-submits the message once ITS upload succeeds — unless another upload
   * is still running, in which case the chip stays in the tray for the normal
   * send button (canSend already gates on every upload being settled).
   */
  private maybeAutoSend(attachment: ChatPendingAttachment): void {
    if (this.autoSendAttachmentId !== attachment.id) return;
    this.autoSendAttachmentId = null;
    if (this.canSend) this.submit();
  }

  /** Drops the auto-send intent when the tracked recording upload fails. */
  private clearAutoSend(attachment: ChatPendingAttachment): void {
    if (this.autoSendAttachmentId === attachment.id) {
      this.autoSendAttachmentId = null;
    }
  }

  /** Cancels the upload task (when running) and releases the thumbnail. */
  private disposeAttachment(attachment: ChatPendingAttachment): void {
    attachment.removed = true;
    if (attachment.status === 'uploading') {
      try {
        attachment.task?.cancel();
      } catch { /* task already settled — nothing to cancel */ }
    }
    this.revokePreview(attachment);
  }

  private revokePreview(attachment: ChatPendingAttachment): void {
    if (!attachment.previewUrl) return;
    try {
      URL.revokeObjectURL(attachment.previewUrl);
    } catch { /* already revoked */ }
    attachment.previewUrl = undefined;
  }

  private generateAttachmentId(): string {
    const cryptoObj: Crypto | undefined = globalThis.crypto;
    if (cryptoObj?.randomUUID) return cryptoObj.randomUUID();
    return `att-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  // ---------------------------------------------------------------------------
  // Voice recording (T-34) — recorder mode over the T-33 upload pipeline
  // ---------------------------------------------------------------------------

  async startRecording(): Promise<void> {
    if (this.disabled || this.recording) return;

    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      // Unsupported browser or insecure (non-HTTPS) context.
      this.alertService.error(this.t('chat.errors.micUnavailable'));
      return;
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      // Localized guidance instead of the raw DOMException (T-34).
      const denied = error instanceof DOMException
        && (error.name === 'NotAllowedError' || error.name === 'SecurityError');
      this.alertService.error(this.t(denied ? 'chat.errors.micPermissionDenied' : 'chat.errors.micUnavailable'));
      return;
    }

    const mimeType = CHAT_AUDIO_MIME_CANDIDATES
      .find((candidate) => MediaRecorder.isTypeSupported?.(candidate));

    let recorder: MediaRecorder;
    try {
      recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    } catch {
      stream.getTracks().forEach((track) => track.stop());
      this.alertService.error(this.t('chat.errors.micUnavailable'));
      return;
    }

    this.mediaStream = stream;
    this.mediaRecorder = recorder;
    this.recordedChunks = [];
    this.recordingDiscarded = false;
    this.recordingAutoSend = false;
    this.recording = true;
    this.recordingSeconds = 0;

    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data?.size > 0) this.recordedChunks.push(event.data);
    };
    // ondataavailable/onstop fire outside the zone on some engines — re-enter
    // so the tray/timeline mutations render.
    recorder.onstop = () => this.zone.run(() => this.finalizeRecording());

    // 1 s timeslice: chunks flush periodically, so a tab crash near the end
    // loses at most the last second instead of the whole recording.
    recorder.start(1000);

    this.recordingTimer = setInterval(() => {
      this.recordingSeconds++;
      if (this.recordingSeconds >= CHAT_RECORDING_MAX_SECONDS) {
        // Hard cap: auto-stop into the tray — never auto-send on the cap.
        this.alertService.info(this.t('chat.composer.recordingMaxReached'));
        this.stopRecorder(false, false);
      }
    }, 1000);

    this.startLevelMeter(stream);
  }

  /**
   * The app went to the background mid-recording (SPEC-2026-09-internal-chat-pwa, T-37 ·
   * RF-27). On a phone that is the common case — a call, the home gesture, the screen
   * locking — and the platform suspends the microphone anyway: what is already recorded is
   * kept as an attachment in the tray (never auto-sent, the user did not ask to send it)
   * and a localized notice says so.
   */
  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (!this.recording || document.visibilityState !== 'hidden') return;
    this.stopRecorder(false, false);
    this.alertService.info(this.t('chat.composer.recordingInterrupted'));
  }

  /** Mic ✓ — stop, upload as an AUDIO attachment and auto-submit on settle. */
  sendRecording(): void {
    this.stopRecorder(false, true);
  }

  /** Mic ✕ — discard everything recorded; no attachment, no message. */
  cancelRecording(): void {
    this.stopRecorder(true, false);
  }

  /** Single stop path for send/cancel/cap/destroy — safe to call repeatedly. */
  private stopRecorder(discard: boolean, autoSend: boolean): void {
    if (!this.mediaRecorder) return;
    this.recordingDiscarded = discard;
    this.recordingAutoSend = autoSend;
    this.recording = false;

    if (this.recordingTimer) {
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;
    }
    this.stopLevelMeter();

    try {
      if (this.mediaRecorder.state !== 'inactive') this.mediaRecorder.stop();
    } catch { /* recorder already stopped */ }
    this.mediaStream?.getTracks().forEach((track) => track.stop());

    // The textarea re-enters the DOM — restore its autogrown height.
    setTimeout(() => this.autoGrow());
  }

  /** MediaRecorder onstop — assembles the blob and feeds the T-33 pipeline. */
  private finalizeRecording(): void {
    const recorder = this.mediaRecorder;
    const chunks = this.recordedChunks;
    this.mediaRecorder = null;
    this.mediaStream = null;
    this.recordedChunks = [];

    if (this.recordingDiscarded || chunks.length === 0) return;

    // Base MIME (parameters stripped) names the blob and maps the extension.
    const mime = (recorder?.mimeType || chunks[0].type || 'audio/webm').split(';')[0];
    const blob = new Blob(chunks, { type: mime });
    if (blob.size === 0) return;

    if (this.attachments.length >= CHAT_MAX_ATTACHMENTS_PER_MESSAGE) {
      this.alertService.error(this.t('chat.errors.tooManyAttachments'));
      return;
    }

    const extension = CHAT_AUDIO_EXTENSION_BY_MIME[mime] ?? '.webm';
    const file = new File([blob], `voice-${Date.now()}${extension}`, { type: mime });
    const attachment = this.startUpload(file);
    if (this.recordingAutoSend) {
      this.autoSendAttachmentId = attachment.id;
    }
  }

  /**
   * Live level hint (T-34): AnalyserNode RMS → the level bar's scaleX. Runs
   * OUTSIDE Angular and mutates the element directly (same precedent as
   * autoGrow) — zero change-detection cost. Purely cosmetic: any failure is
   * swallowed and recording proceeds without the hint.
   */
  private startLevelMeter(stream: MediaStream): void {
    const AudioContextCtor: typeof AudioContext | undefined =
      globalThis.AudioContext ?? (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) return;

    try {
      this.audioContext = new AudioContextCtor();
      const source = this.audioContext.createMediaStreamSource(stream);
      const analyser = this.audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);

      this.zone.runOutsideAngular(() => {
        const tick = () => {
          analyser.getByteTimeDomainData(data);
          let sum = 0;
          for (const sample of data) {
            const normalized = (sample - 128) / 128;
            sum += normalized * normalized;
          }
          const rms = Math.sqrt(sum / data.length);
          const level = Math.max(0.05, Math.min(1, rms * 3));
          const element = this.recorderLevel?.nativeElement;
          if (element) element.style.transform = `scaleX(${level})`;
          this.levelRafId = requestAnimationFrame(tick);
        };
        this.levelRafId = requestAnimationFrame(tick);
      });
    } catch { /* level hint only — recording works without it */ }
  }

  private stopLevelMeter(): void {
    if (this.levelRafId !== null) {
      cancelAnimationFrame(this.levelRafId);
      this.levelRafId = null;
    }
    if (this.audioContext) {
      this.audioContext.close().catch(() => { /* already closed */ });
      this.audioContext = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Formatting toolbar (markdown subset — wrap-selection behavior)
  // ---------------------------------------------------------------------------

  toggleToolbar(): void {
    this.toolbarVisible = !this.toolbarVisible;
    try {
      localStorage.setItem(TOOLBAR_STORAGE_KEY, this.toolbarVisible ? '1' : '0');
    } catch { /* storage unavailable — preference just not persisted */ }
  }

  formatBold(): void { this.wrapSelection('**'); }
  formatItalic(): void { this.wrapSelection('_'); }
  formatUnderline(): void { this.wrapSelection('__'); }
  formatStrike(): void { this.wrapSelection('~~'); }
  formatInlineCode(): void { this.wrapSelection('`'); }
  formatCodeBlock(): void { this.wrapSelection('```\n', '\n```'); }

  formatLink(): void {
    const element = this.composerInput?.nativeElement;
    if (!element) return;
    const start = element.selectionStart ?? this.draft.length;
    const end = element.selectionEnd ?? start;
    const selected = this.draft.slice(start, end);

    const insertion = `[${selected}](https://)`;
    if (!this.replaceRange(start, end, insertion)) return;

    if (selected) {
      // Select the "https://" placeholder so typing replaces it with the URL.
      const urlStart = start + 1 + selected.length + 2;
      this.setSelection(urlStart, urlStart + 'https://'.length);
    } else {
      this.setSelection(start + 1, start + 1);
    }
  }

  formatOrderedList(): void { this.applyList(true); }
  formatUnorderedList(): void { this.applyList(false); }

  /** `@` toolbar shortcut — inserts the trigger and opens the autocomplete. */
  insertMentionTrigger(): void {
    this.insertAtCaret('@');
  }

  insertEmoji(emoji: string): void {
    this.insertAtCaret(emoji);
  }

  // ---------------------------------------------------------------------------
  // Mention autocomplete
  // ---------------------------------------------------------------------------

  selectMention(option: ChatMentionMember | undefined): void {
    const element = this.composerInput?.nativeElement;
    if (!option || !element || this.mentionStartIndex < 0) {
      this.closeMention();
      return;
    }

    const caret = element.selectionStart ?? this.draft.length;
    const insertion = `@${option.name} `;
    const start = this.mentionStartIndex;
    this.closeMention();
    if (!this.replaceRange(start, caret, insertion)) return;

    if (!this.pendingMentions.some((mention) => mention.id === option.id)) {
      this.pendingMentions.push({ id: option.id, name: option.name });
    }
    this.setSelection(start + insertion.length, start + insertion.length);
  }

  private updateMentionState(): void {
    const element = this.composerInput?.nativeElement;
    if (!element || this.members.length === 0) {
      this.closeMention();
      return;
    }

    const caret = element.selectionStart ?? this.draft.length;
    const upToCaret = this.draft.slice(0, caret);
    const atIndex = upToCaret.lastIndexOf('@');
    if (atIndex === -1) {
      this.closeMention();
      return;
    }

    // The `@` must start a word (line start or preceded by whitespace/"(").
    const before = atIndex > 0 ? upToCaret.charAt(atIndex - 1) : '';
    if (before && !/[\s(]/.test(before)) {
      this.closeMention();
      return;
    }

    const query = upToCaret.slice(atIndex + 1);
    if (query.includes('\n') || query.length > MENTION_MAX_QUERY_LENGTH) {
      this.closeMention();
      return;
    }

    const normalizedQuery = this.normalize(query);
    const options = this.members
      .filter((member) => this.normalize(member.name).includes(normalizedQuery))
      .slice(0, MENTION_MAX_OPTIONS);

    if (options.length === 0) {
      this.closeMention();
      return;
    }

    this.mentionStartIndex = atIndex;
    this.mentionOptions = options;
    this.mentionActiveIndex = Math.min(this.mentionActiveIndex, options.length - 1);
    this.mentionOpen = true;
  }

  private closeMention(): void {
    this.mentionOpen = false;
    this.mentionOptions = [];
    this.mentionActiveIndex = 0;
    this.mentionStartIndex = -1;
  }

  private normalize(value: string): string {
    return (value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  }

  // ---------------------------------------------------------------------------
  // Text edition helpers
  // ---------------------------------------------------------------------------

  /**
   * Wraps the selection with the markers (or unwraps when already wrapped —
   * inside the selection or immediately around it). No selection inserts the
   * empty pair with the caret in the middle.
   */
  private wrapSelection(prefix: string, suffix: string = prefix): void {
    const element = this.composerInput?.nativeElement;
    if (!element || this.disabled) return;

    const start = element.selectionStart ?? this.draft.length;
    const end = element.selectionEnd ?? start;
    const selected = this.draft.slice(start, end);
    const before = this.draft.slice(0, start);
    const after = this.draft.slice(end);

    if (selected.length >= prefix.length + suffix.length
      && selected.startsWith(prefix) && selected.endsWith(suffix)) {
      const inner = selected.slice(prefix.length, selected.length - suffix.length);
      if (!this.replaceRange(start, end, inner)) return;
      this.setSelection(start, start + inner.length);
      return;
    }

    if (before.endsWith(prefix) && after.startsWith(suffix)) {
      if (!this.replaceRange(start - prefix.length, end + suffix.length, selected)) return;
      this.setSelection(start - prefix.length, start - prefix.length + selected.length);
      return;
    }

    if (!this.replaceRange(start, end, `${prefix}${selected}${suffix}`)) return;
    this.setSelection(start + prefix.length, start + prefix.length + selected.length);
  }

  /** Prefixes (or unprefixes — toggle) the selected lines as a list. */
  private applyList(ordered: boolean): void {
    const element = this.composerInput?.nativeElement;
    if (!element || this.disabled) return;

    const start = element.selectionStart ?? 0;
    const end = element.selectionEnd ?? start;
    const lineStart = this.draft.lastIndexOf('\n', Math.max(start - 1, 0)) + 1;
    let lineEnd = this.draft.indexOf('\n', end);
    if (lineEnd === -1) lineEnd = this.draft.length;

    const lines = this.draft.slice(lineStart, lineEnd).split('\n');
    const marker = ordered ? /^\d+\.\s/ : /^-\s/;
    const allPrefixed = lines.every((line) => marker.test(line));

    const next = allPrefixed
      ? lines.map((line) => line.replace(marker, '')).join('\n')
      : lines.map((line, index) => (ordered ? `${index + 1}. ${line}` : `- ${line}`)).join('\n');

    if (!this.replaceRange(lineStart, lineEnd, next)) return;
    this.setSelection(lineStart + next.length, lineStart + next.length);
  }

  private insertAtCaret(text: string): void {
    const element = this.composerInput?.nativeElement;
    const start = element?.selectionStart ?? this.draft.length;
    const end = element?.selectionEnd ?? start;
    if (!this.replaceRange(start, end, text)) return;
    this.setSelection(start + text.length, start + text.length);
  }

  /** Applies an edit honoring the char limit; returns whether it was applied. */
  private replaceRange(start: number, end: number, insertion: string): boolean {
    if (this.disabled) return false;
    const next = this.draft.slice(0, start) + insertion + this.draft.slice(end);
    if (next.length > this.maxLength) return false;
    this.draft = next;
    return true;
  }

  /** Restores focus/selection after the model write lands in the DOM. */
  private setSelection(start: number, end: number): void {
    setTimeout(() => {
      const element = this.composerInput?.nativeElement;
      if (!element) return;
      element.focus();
      element.setSelectionRange(start, end);
      this.autoGrow();
      this.updateMentionState();
      this.draftChange.emit();
      this.typingChange.emit(this.draft.trim().length > 0);
    });
  }

  /** Grows the textarea with the content up to the SCSS max-height. */
  private autoGrow(): void {
    const element = this.composerInput?.nativeElement;
    if (!element) return;
    element.style.height = 'auto';
    const style = getComputedStyle(element);
    const borders =
      parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
    element.style.height = `${element.scrollHeight + borders}px`;
  }

  private observeWidth(): void {
    const element = this.composerInput?.nativeElement;
    if (!element || typeof ResizeObserver === 'undefined') return;

    this.zone.runOutsideAngular(() => {
      this.widthObserver = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width ?? 0;
        if (width === this.lastWidth) return;
        this.lastWidth = width;
        this.autoGrow();
      });
      this.widthObserver.observe(element);
    });
  }

  private readToolbarPreference(): boolean {
    try {
      return localStorage.getItem(TOOLBAR_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }
}
